import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar2 from '../component/navbar2';
import Footer from '../components/BrandFooter';
import { BRAND } from '@/brand';
import '../styles/publicPages.css';

// Decorative glow — faint gold on corporate, faint blue on consulting.
const GLOW = BRAND === 'corporate' ? 'rgba(255,213,79,.14)' : 'rgba(25,118,210,.10)';

const SECTIONS = [
  {
    title: 'Introduction',
    content: (
      <p>
        Welcome to Conncct ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your data when you interact with our website, platform, and services. By accessing or using our website or services, you consent to the practices described in this Privacy Policy. If you do not agree, please discontinue use of our services.
      </p>
    ),
  },
  {
    title: 'Information We Collect',
    content: (
      <>
        <p style={{ marginBottom: 14 }}>We may collect the following types of information:</p>
        <p style={{ fontWeight: 700, marginBottom: 8, color: '#17263A' }}>1. Personal Information You Provide</p>
        <p style={{ marginBottom: 10 }}>When you submit forms, register, contact us, or otherwise interact with our platform, we may collect:</p>
        <ul>
          <li>Full name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Company or business information</li>
          <li>Any information you voluntarily submit (e.g., messages, attachments)</li>
        </ul>
        <p style={{ fontWeight: 700, margin: '18px 0 8px', color: '#17263A' }}>2. Automatically Collected Information</p>
        <p style={{ marginBottom: 10 }}>When you access our website, we may collect:</p>
        <ul>
          <li>IP address</li>
          <li>Browser type &amp; version</li>
          <li>Device and OS information</li>
          <li>Pages viewed &amp; site interaction behavior</li>
          <li>Cookies and tracking technologies (only with clear consent where applicable)</li>
        </ul>
      </>
    ),
  },
  {
    title: 'How We Use Your Information',
    content: (
      <>
        <p style={{ marginBottom: 10 }}>We may use your personal information to:</p>
        <ul>
          <li>Provide and improve our Services</li>
          <li>Respond to your inquiries and support requests</li>
          <li>Personalize your user experience</li>
          <li>Analyze website performance and usage</li>
          <li>Communicate updates, offers, and marketing content (with opt-out rights)</li>
          <li>Conduct compliance, risk, and security checks</li>
          <li>Fulfill legal, regulatory, and contractual obligations</li>
        </ul>
        <p style={{ marginTop: 14 }}>We do not sell or trade your personal data to third-party marketers.</p>
      </>
    ),
  },
  {
    title: 'How We Share Your Information',
    content: (
      <>
        <p style={{ marginBottom: 10 }}>We may share your information only in the following cases:</p>
        <ul>
          <li><strong>Service Providers:</strong> Hosting, analytics, marketing tools, CRM, payment gateway providers, and IT partners who support our business</li>
          <li><strong>Legal Compliance:</strong> Government, regulators, or law enforcement when required</li>
          <li><strong>Business Transactions:</strong> In case of merger, acquisition, restructuring, or investment process</li>
          <li><strong>With Your Consent:</strong> When you explicitly authorize us</li>
        </ul>
        <p style={{ margin: '14px 0 18px' }}>Each third-party provider is required to protect your information and use it only for the services we engage them for.</p>
        <p style={{ fontWeight: 700, marginBottom: 8, color: '#17263A' }}>Your Rights</p>
        <p style={{ marginBottom: 10 }}>You have the right to:</p>
        <ul>
          <li>Request access to your personal data</li>
          <li>Request correction or deletion of your information</li>
          <li>Object to processing or withdraw consent (where applicable)</li>
          <li>Request data portability (ADGM/GDPR equivalent rights)</li>
          <li>Opt-out of marketing communications at any time</li>
        </ul>
        <p style={{ marginTop: 14 }}>
          To exercise these rights, contact us at:{' '}
          <a href="mailto:support@conncct.com" style={{ color: '#1976D2', fontWeight: 700 }}>support@conncct.com</a>
        </p>
      </>
    ),
  },
  {
    title: 'Data Security',
    content: (
      <>
        <p style={{ marginBottom: 14 }}>
          We implement administrative, technical, and physical safeguards to protect your data. These include encryption, secure servers, restricted access, and industry-standard security protocols.
        </p>
        <p style={{ marginBottom: 18 }}>
          However, no internet transmission or storage method is fully secure. We cannot guarantee absolute security but continuously work to enhance our controls.
        </p>
        <p style={{ fontWeight: 700, marginBottom: 8, color: '#17263A' }}>Cookies &amp; Tracking Technologies</p>
        <p style={{ marginBottom: 10 }}>We use cookies and similar technologies to:</p>
        <ul>
          <li>Enhance your website experience</li>
          <li>Analyze traffic and usage behavior</li>
          <li>Support marketing and performance optimization</li>
        </ul>
        <p style={{ margin: '14px 0 18px' }}>You may manage cookie preferences through your browser settings.</p>
        <p style={{ fontWeight: 700, marginBottom: 8, color: '#17263A' }}>International Data Transfers</p>
        <p>
          Your data may be processed or stored across multiple jurisdictions, including the UAE, ADGM, and the United States. We ensure appropriate legal and security protections for such transfers.
        </p>
      </>
    ),
  },
  {
    title: 'Updates to This Policy',
    content: (
      <p>
        We may update this policy periodically. Changes will be posted here with a revised "Last Updated" date. Continued use of our services constitutes acceptance of any changes.
      </p>
    ),
  },
  {
    title: 'Contact Us',
    content: (
      <>
        <p style={{ marginBottom: 10 }}>If you have questions about this Privacy Policy or how your data is handled, please contact:</p>
        <p style={{ marginBottom: 6 }}>
          Email:{' '}
          <a href="mailto:support@conncct.com" style={{ color: '#1976D2', fontWeight: 700 }}>support@conncct.com</a>
        </p>
        <p>
          Website:{' '}
          <a href="https://www.conncct.com" style={{ color: '#1976D2', fontWeight: 700 }}>www.conncct.com</a>
        </p>
      </>
    ),
  },
  {
    title: 'Governing Law',
    content: (
      <>
        <p style={{ marginBottom: 10 }}>This Policy is governed by the laws and data-protection regulations of the United States of America.</p>
        <p>Where applicable, it also aligns with international privacy standards such as GDPR principles.</p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="pub-page">
      <div style={{ position: 'sticky', top: 0, zIndex: 50 }}>
        <Navbar2 bgColor="white" logoTo="/" />
      </div>

      {/* ── HERO ── */}
      <section className="pub-hero navy" style={{ textAlign: 'center' }}>
        <div className="pub-hero-dots" aria-hidden="true" />
        <div
          className="pub-hero-glow"
          style={{ top: -120, left: '50%', transform: 'translateX(-50%)', width: 600, height: 380, background: `radial-gradient(ellipse, ${GLOW} 0%, transparent 70%)` }}
          aria-hidden="true"
        />
        <div className="pub-inner narrow" style={{ position: 'relative', zIndex: 1 }}>
          <span className="pub-eye light" style={{ marginBottom: 22, display: 'inline-flex' }}>Legal</span>
          <h1 className="pub-h1" style={{ color: '#fff', marginBottom: 20 }}>Privacy Policy</h1>
          <p className="pub-lead light" style={{ margin: '0 auto', textAlign: 'center', maxWidth: 500 }}>
            How Conncct collects, uses, and protects your information.
          </p>
        </div>
      </section>

      {/* ── QUICK NAV ── */}
      <section className="pub-sec cream" style={{ paddingTop: 52, paddingBottom: 52 }}>
        <div className="pub-inner narrow">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {SECTIONS.map(({ title }) => (
              <a
                key={title}
                href={`#${title.toLowerCase().replace(/\s+/g, '-')}`}
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  height: 34, padding: '0 14px',
                  borderRadius: 999, border: '1.5px solid #E8E2D2',
                  fontSize: 12.5, fontWeight: 600, color: '#42505F',
                  textDecoration: 'none', background: '#fff',
                  transition: 'border-color .15s, color .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#E6B400'; e.currentTarget.style.color = '#17263A'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#E8E2D2'; e.currentTarget.style.color = '#42505F'; }}
              >
                {title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTENT ── */}
      <section className="pub-sec white" style={{ paddingTop: 20 }}>
        <div className="pub-inner narrow">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {SECTIONS.map(({ title, content }, i) => (
              <div
                key={title}
                id={title.toLowerCase().replace(/\s+/g, '-')}
                style={{
                  padding: '36px 40px',
                  borderRadius: 20,
                  border: '1.5px solid #E8E2D2',
                  marginBottom: 16,
                  background: '#fff',
                  scrollMarginTop: 80,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#E6B400', width: 28, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h2 style={{ margin: 0, fontWeight: 800, fontSize: 20, color: '#17263A', letterSpacing: '-.02em' }}>{title}</h2>
                </div>
                <div
                  style={{ fontSize: 14.5, color: '#42505F', lineHeight: 1.8, paddingLeft: 42 }}
                  className="privacy-body"
                >
                  {content}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="pub-sec tinted">
        <div className="pub-inner narrow">
          <div style={{
            background: '#17263A', borderRadius: 24, padding: '44px 48px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 24, flexWrap: 'wrap', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: -60, right: -60, width: 300, height: 240, background: `radial-gradient(ellipse, ${GLOW} 0%, transparent 70%)`, pointerEvents: 'none' }} aria-hidden="true" />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <p style={{ margin: 0, fontWeight: 800, fontSize: 18, color: '#fff', marginBottom: 6 }}>Have privacy questions?</p>
              <p style={{ margin: 0, fontSize: 14, color: 'rgba(206,219,242,.8)' }}>Our team is happy to walk you through how we handle your data.</p>
            </div>
            <Link to="/contact" className="pub-btn gold" style={{ position: 'relative', zIndex: 1, flexShrink: 0 }}>
              Contact us →
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        .privacy-body ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
        .privacy-body li { display: flex; align-items: flex-start; gap: 10px; }
        .privacy-body li::before { content: "→"; color: #E6B400; font-weight: 700; flex-shrink: 0; }
        .privacy-body p { margin: 0 0 10px; }
        .privacy-body p:last-child { margin: 0; }
      `}</style>
    </div>
  );
}
